/**
 * Per-atom class-flag classification — the TypeScript port of
 * `SelectorClassifyAtoms` (`packages/engine/layer3/Selector.cpp:1090-1370`).
 *
 * PyMOL tags every atom with one of the mutually-exclusive "class" flags in the
 * high five bits of `AtomInfoType.flags` (`packages/engine/layer2/AtomInfo.h`):
 * polymer/protein, polymer/nucleic, solvent, organic or inorganic. The tag is
 * assigned per residue: first by a residue-name fast path
 * (`AtomInfoKnownProteinResName` / `…NucleicResName` / `…WaterResName`,
 * `AtomInfo.cpp:479-785`), then by a structural fallback that inspects the
 * residue's element composition and backbone connectivity.
 *
 * The classification is observable through `cmd.get_model` (each chempy atom's
 * `flags`), which is why the port needs it: a bare carbon pseudoatom, for
 * example, classifies as `organic` (`cAtomFlag_organic`, 0x20000000).
 */

import type { AtomInfo } from './atom';
import type { ObjectMolecule } from './molecule';

/* --------------------------- class-flag constants ------------------------- */
// packages/engine/layer2/AtomInfo.h
export const cAtomFlag_protein = 0x00000040;
export const cAtomFlag_nucleic = 0x00000080;
export const cAtomFlag_organic = 0x20000000;
export const cAtomFlag_inorganic = 0x40000000;
export const cAtomFlag_polymer = 0x08000000;
export const cAtomFlag_solvent = 0x10000000;
/** Low 27 bits — everything BUT the class flags (`cAtomFlag_class_mask`). */
export const cAtomFlag_class_mask = 0x07ffffff;

/* --------------------------- known residue names -------------------------- */
// AtomInfoKnownProteinResName (AtomInfo.cpp:594-785)
const PROTEIN_RESN = new Set([
  'ALA', 'ARG', 'ASP', 'ASN', 'CYS', 'CYX', 'GLN', 'GLU', 'GLY', 'HIS', 'HID',
  'HIE', 'HIP', 'ILE', 'LEU', 'LYS', 'MET', 'MSE', 'PHE', 'PRO', 'PTR', 'SER',
  'THR', 'TRP', 'TYR', 'VAL',
]);
// AtomInfoKnownPNAResName (AtomInfo.cpp:587-592)
const PNA_RESN = new Set(['APN', 'CPN', 'GPN', 'TPN', 'IPN']);
// AtomInfoKnownWaterResName (AtomInfo.cpp:479-551)
const WATER_RESN = new Set(['HOH', 'H2O', 'DOD', 'TIP', 'T3P', 'T4P', 'WAT', 'SOL', 'SPC']);

/** AtomInfoKnownNucleicResName (AtomInfo.cpp:562-585). */
function knownNucleicResName(resn: string): boolean {
  if (PNA_RESN.has(resn)) return true;
  let r = resn;
  if (r[0] === 'D') r = r.slice(1); // deoxy ribonucleotide
  return r.length === 1 && 'ACGITU'.includes(r);
}

/* ------------------------------ element helpers --------------------------- */
function isHydrogen(a: AtomInfo): boolean {
  const e = a.elem.toUpperCase();
  return e === 'H' || e === 'D';
}

/* --------------------------- residue delimitation ------------------------- */
/**
 * A residue key mirroring `AtomInfoSameResidue` (`AtomInfo.cpp`): two atoms are
 * in the same residue when resv, chain, hetatm, segi and resn all match (the
 * insertion code lives inside `resi` here).
 */
function residueKey(a: AtomInfo): string {
  return `${a.resv} ${a.chain} ${a.hetatm ? 1 : 0} ${a.segi} ${a.resi} ${a.resn}`;
}

/* ----------------------------- classification ----------------------------- */

/**
 * Compute the class-flag mask for every atom of `mol`, keyed by atom index.
 * Returns one mask per atom (the high class bits only — merge with the atom's
 * own low-bit user flags via `cAtomFlag_class_mask`). Ports the residue loop of
 * `SelectorClassifyAtoms`.
 */
export function classifyMolecule(mol: ObjectMolecule): number[] {
  const atoms = mol.atoms;
  const n = atoms.length;
  const masks = new Array<number>(n).fill(0);

  // Name-adjacency: is atom `idx` bonded to a neighbour named `name`?
  const bondedToName = (idx: number, name: string): boolean => {
    for (const [a, b] of mol.bonds) {
      if (a === idx && atoms[b]?.name === name) return true;
      if (b === idx && atoms[a]?.name === name) return true;
    }
    return false;
  };

  let i = 0;
  while (i < n) {
    // Delimit the contiguous residue [i, j).
    const key = residueKey(atoms[i]!);
    let j = i + 1;
    while (j < n && residueKey(atoms[j]!) === key) j++;

    const head = atoms[i]!;
    const resn = head.resn.toUpperCase();
    let mask = 0;

    if (!head.hetatm && PROTEIN_RESN.has(resn)) {
      mask = cAtomFlag_polymer | cAtomFlag_protein;
    } else if ((!head.hetatm || PNA_RESN.has(resn)) && knownNucleicResName(resn)) {
      mask = cAtomFlag_polymer | cAtomFlag_nucleic;
    } else if (WATER_RESN.has(resn)) {
      mask = cAtomFlag_solvent;
    } else {
      // Structural fallback (Selector.cpp:1136-1285).
      let foundOnlyH = true;
      let foundCa = false, foundN = false, foundC = false, foundO = false, foundOh2 = false;
      let foundCarbon = false, foundCnBond = false, foundNcBond = false;
      let foundO3star = false, foundC3star = false, foundC4star = false, foundC5star = false;
      let foundO5star = false, foundO3Bond = false, foundPBond = false;

      for (let a = i; a < j; a++) {
        const ai = atoms[a]!;
        const e = ai.elem.toUpperCase();
        const nm = ai.name;
        if (e === 'C') {
          foundCarbon = true;
          if (nm === 'C') {
            foundC = true;
            foundCnBond = bondedToName(a, 'N');
          } else if (nm === 'CA') {
            foundCa = true;
          } else if (nm === 'C3*' || nm === "C3'") {
            foundC3star = true;
          } else if (nm === 'C4*' || nm === "C4'") {
            foundC4star = true;
          } else if (nm === 'C5*' || nm === "C5'") {
            foundC5star = true;
          }
        } else if (e === 'N') {
          if (nm === 'N') {
            foundN = true;
            foundNcBond = bondedToName(a, 'C');
          }
        } else if (e === 'O') {
          if (nm === 'O') foundO = true;
          else if (nm === 'OH2') foundOh2 = true;
          else if (nm === 'O3*' || nm === "O3'") {
            foundO3star = true;
            foundO3Bond = bondedToName(a, 'P');
          } else if (nm === 'O5*' || nm === "O5'") {
            foundO5star = true;
          }
        } else if (e === 'P') {
          if (nm === 'P') {
            foundPBond = bondedToName(a, 'O3*') || bondedToName(a, "O3'");
          }
        }
        if (!isHydrogen(ai)) foundOnlyH = false;
      }

      if (foundCa && foundN && foundC && foundO && (foundCnBond || foundNcBond)) {
        mask = cAtomFlag_polymer | cAtomFlag_protein;
      } else if (
        foundO3star && foundC3star && foundC4star && foundC5star && foundO5star &&
        (foundO3Bond || foundPBond)
      ) {
        mask = cAtomFlag_polymer | cAtomFlag_nucleic;
      } else if (foundCarbon) {
        mask = cAtomFlag_organic;
      } else if ((foundO || foundOh2) && j - i === 1) {
        mask = cAtomFlag_solvent;
      } else if (!foundOnlyH) {
        mask = cAtomFlag_inorganic;
      }
    }

    for (let a = i; a < j; a++) masks[a] = mask;
    i = j;
  }

  return masks;
}
